import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { SnackbarProvider } from 'notistack';
import { useTranslation } from 'react-i18next';
import { RequiredAuthProvider } from '@propelauth/react';

import M3 from '@M3/M3';
import DisplaySettings from '@Providers/DisplaySettings';
import ApiProvider from '@Providers/ApiProvider';
import {
    createRouteMask,
    createRouter,
    RouterProvider,
} from '@tanstack/react-router';
import Loading from '@Components/General/Feedback/Loading';
import DialogProvider from '@Providers/DialogProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import setMutationDefaults from '@utils/mutationDefaults';
import { routeTree } from '@routeTree.gen';
import ErrorComponent from '@Components/General/Feedback/ErrorComponent';

const rtlCache = createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin],
});

const ltrCache = createCache({
    key: 'mui',
});

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: false,
        },
    },
});

setMutationDefaults(queryClient);

const routeMasks = createRouteMask({
    routeTree,
    // search: ({ page, page_size, ordering, ...rest }) => ({ ...rest }),
    search: prev => ({
        ...prev,
        page: undefined,
    }),
    // search: ({ ...params }) => ({ ...params }),
    from: '/contacts',
    to: '/contacts',
});

const router = createRouter({
    routeTree,
    context: { queryClient },
    // defaultPendingComponent: Loading,
    defaultErrorComponent: ErrorComponent,
    defaultNotFoundComponent: () => <h1>Not Found</h1>,
    scrollToTopSelectors: ['#main-content'],
    scrollRestoration: true,
    getScrollRestorationKey: location => location.href,
    // defaultComponent: () => <h1>Default</h1>,
    // routeMasks: [routeMasks],
});

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
}

function App() {
    const { i18n } = useTranslation();
    const rtl = i18n.dir() === 'rtl';

    return (
        <QueryClientProvider client={queryClient}>
            <CacheProvider value={rtl ? rtlCache : ltrCache}>
                <M3>
                    <RequiredAuthProvider
                        authUrl={import.meta.env.VITE_AUTH_URL}
                        displayWhileLoading={<Loading size={192} />}
                        // displayWhileLoading={<h1>Loading Auth...</h1>}
                    >
                        <ApiProvider router={router}>
                            <DisplaySettings>
                                <DialogProvider>
                                    <RouterProvider router={router} />
                                </DialogProvider>
                            </DisplaySettings>
                        </ApiProvider>
                    </RequiredAuthProvider>
                </M3>
            </CacheProvider>
            {localStorage.dev !== 'off' && (
                <ReactQueryDevtools position="right" />
            )}
        </QueryClientProvider>
    );
}

export default App;
